import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CurrencyProvider } from "./contexts/CurrencyContext";
import { Toaster } from "./components/ui/sonner";
import SitePopup from "./components/SitePopup";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import ForumPage from "./pages/ForumPage";
import ForumPostPage from "./pages/ForumPostPage";
import EventsPage from "./pages/EventsPage";
import LibraryPage from "./pages/LibraryPage";
import ConnectionsPage from "./pages/ConnectionsPage";
import ProfilePage from "./pages/ProfilePage";
import MembershipPage from "./pages/MembershipPage";
import AdminPage from "./pages/AdminPage";
import CustomPageView from "./pages/CustomPageView";
import PillarsPage from "./pages/PillarsPage";
import PillarProfilePage from "./pages/PillarProfilePage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import MembershipWelcomePage from "./pages/MembershipWelcomePage";

// Protected Route Component
function ProtectedRoute({ children }) {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F9F6F0] flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-[#5B2D8E] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="font-serif text-[#4A4A68]">Loading...</p>
                </div>
            </div>
        );
    }

    if (!user || user === false) {
        return <Navigate to="/login" replace />;
    }

    return children;
}

// Member Only Route (requires membership)
function MemberRoute({ children }) {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F9F6F0] flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-[#5B2D8E] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="font-serif text-[#4A4A68]">Loading...</p>
                </div>
            </div>
        );
    }

    if (!user || user === false) {
        return <Navigate to="/login" replace />;
    }

    if (!user.membership_tier) {
        return <Navigate to="/membership" replace />;
    }

    return children;
}

function AppRoutes() {
    return (
        <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage mode="reset" />} />
            <Route path="/set-password" element={<ResetPasswordPage mode="set-password" />} />
            <Route path="/membership-welcome" element={<MembershipWelcomePage />} />
            <Route path="/membership" element={<MembershipPage />} />
            <Route
                path="/dashboard"
                element={
                    <ProtectedRoute>
                        <DashboardPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/forum"
                element={
                    <MemberRoute>
                        <ForumPage />
                    </MemberRoute>
                }
            />
            <Route
                path="/forum/:postId"
                element={
                    <MemberRoute>
                        <ForumPostPage />
                    </MemberRoute>
                }
            />
            <Route
                path="/events"
                element={<EventsPage />}
            />
            <Route
                path="/library"
                element={<LibraryPage />}
            />
            <Route
                path="/connections"
                element={
                    <MemberRoute>
                        <ConnectionsPage />
                    </MemberRoute>
                }
            />
            <Route
                path="/profile"
                element={
                    <ProtectedRoute>
                        <ProfilePage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin"
                element={
                    <ProtectedRoute>
                        <AdminPage />
                    </ProtectedRoute>
                }
            />
            <Route path="/p/:slug" element={<CustomPageView />} />
            <Route path="/pillars" element={<PillarsPage />} />
            <Route path="/pillars/:id" element={<PillarProfilePage />} />
        </Routes>
    );
}

function App() {
    return (
        <AuthProvider>
            <CurrencyProvider>
                <BrowserRouter>
                    <AppRoutes />
                    <SitePopup />
                    <Toaster position="top-right" />
                </BrowserRouter>
            </CurrencyProvider>
        </AuthProvider>
    );
}

export default App;
